import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import PropTypes from 'prop-types';
import {useParams} from "react-router-dom";
import {AUcallout} from '@gov.au/callout';

import {
    getFormDataSelector,
    getFormPropertiesSelector,
    getFormServiceStatusSelector
} from '../FormStore/Selectors/FormDataSelectors';
import {getB2cSettingsSelector} from '../PortalStore/Selectors/SystemSettingSelectors';
import {addPageAlert} from '../PortalStore/Actions/PageAlertActions';
import {getSubmissionDetailsFromSubmissionId} from '../FormStore/Actions/FormServiceActions/CancellationReason/GetSubmissionDetailsFromSubmissionId';

import CASASpinner from '../components/Portal/CASASpinner';
import Application from '../components/CASAForm/Application';
import Step from '../components/CASAForm/Step';
import Question from '../components/CASAForm/Question';
import SuccessScreen from '../pages/SuccessScreen';
import {requestCloseSubmissionCrmAction} from "../FormStore/Actions/FormServiceActions/CancellationReason/RequestCloseSubmissionCrmAction";

const SubmissionCancellation = () => {
    const dispatch = useDispatch();
    const {organisationId, submissionId} = useParams()
    const [loaderActive, setLoaderActive] = useState(true);
    const [submissionLoader, setSubmissionLoader] = useState(false);
    const [formEnabled, setFormEnabled] = useState(false);
    const [caseNumber, setCaseNumber] = useState(false);

    // Redux store properties
    const B2CSettings = useSelector(state => getB2cSettingsSelector(state.B2CSettings, ['webappurlFormsApi', 'accesstokenForms', 'parsed']));
    const submissionDetails = useSelector(state => getFormServiceStatusSelector(state.formStore, 'cancellationSubmissionDetails'));
    const formProperties = useSelector(state => getFormPropertiesSelector(state.formStore));
    const formData = useSelector(state => getFormDataSelector(state.formStore, 'genericFormData'));
    const requestCloseSubmission = useSelector(state => getFormServiceStatusSelector(state.formStore, 'requestCloseSubmission'));
    const organisation = useSelector(state => state.getOrganisation.organisation);
    const userDetails = useSelector(state => state.userdetails.items?.value[0]);

    // Query submission details if they don't already exist
    useEffect(() => {
        dispatch(getSubmissionDetailsFromSubmissionId(B2CSettings.webappurlFormsApi, B2CSettings.accesstokenForms, submissionId))
    }, []);

    // Handle successful response from get Submission Details service
    useEffect(() => {
        if (submissionDetails.wasSuccessful) {
            if (submissionDetails?.data?.submissionStatus === "InProgress") {
                setFormEnabled(true);
                setCaseNumber("N/A")
                let caseNumberObj = submissionDetails.data?.serviceRequestData.filter (item => item.name === "TransactionId");
                if (caseNumberObj !== undefined && caseNumberObj.length === 1) {
                    setCaseNumber(caseNumberObj[0].value);
                }
            } else {
                setFormEnabled(false);
                dispatch(addPageAlert({
                    level: "error",
                    title: "Unable to complete application online",
                    message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
                }));
            }
            setLoaderActive(false);
        } else if (submissionDetails.errors) {
            // TODO: This error message doesn't make sense
            setFormEnabled(false);
            dispatch(addPageAlert({
                level: "error",
                title: "Unable to complete application online",
                message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
            }));
            setLoaderActive(false);
        }
    }, [submissionDetails]);

    useEffect(() => {
        if (requestCloseSubmission.wasSuccessful) {
            dispatch(addPageAlert({
                level: 'success',
                title: 'Submission cancelled',
                message: submissionDetails?.data?.serviceCatalogueName + ' has been cancelled'
            }));
            setSubmissionLoader(false);
        }

        if (requestCloseSubmission.errors) {
            // TODO: Is this error message correct
            dispatch(addPageAlert({
                level: "error",
                title: "Unable to complete application online",
                message: 'Unfortunately this application cannot be completed online. Please contact us for further information.'
              }));
            setSubmissionLoader(false);
        }
    }, [requestCloseSubmission]);

    // Submit on button click
    useEffect(() => {
        if (formProperties.submitForm) {
            submissionCancellation();
        }
    }, [formProperties.submitForm]);

    // Submision service
    const submissionCancellation = async () => {
        setSubmissionLoader(true);

        if (formData) {
            const questions = formData.steps[0].questions;
            const cancellationReason = Object.values(questions).find(question => question.id === 'cancellationReason')?.answer?.value;
            dispatch(requestCloseSubmissionCrmAction(
                B2CSettings.webappurlFormsApi,
                B2CSettings.accesstokenForms,
                formProperties,
                formData,
                submissionId,
                cancellationReason,
                caseNumber)
            )
        }
    }

    if (loaderActive) {
        return <CASASpinner/>
    }

    if (requestCloseSubmission.wasSuccessful) {
        return (
            <SuccessScreen>
                <div>
                    <p>Thank you,</p>
                    <p>Your {submissionDetails.data.serviceCatalogueName} submission has been cancelled.</p>
                    <p>An email confirming the cancellation of the submission has been sent to your {organisationId && 'organisations'} email address <strong>{organisationId ? organisation?.emailaddress1 : userDetails?.emailaddress1}</strong>.</p>
                </div>
            </SuccessScreen>
        );
    }

    return (
        <>
            {requestCloseSubmission.loading && <CASASpinner message="Submitting form"/>}
            <Application
                formInformation={{
                    id: "cancelSubmission",
                    name: "Cancel submission",
                    title: "Cancel submission",
                    formDataType: "genericFormData",
                    version: "01",
                    rootFormCode: "Cancel_Application",
                    formCode: "Cancel_Application",
                    organisationId: organisationId && organisation.accountid,
                    organisationArn: organisationId && organisation.casa_arn
                }}
                enableSaveButton={false}
                showback={false}
                enableForm={formEnabled}>

                <Step
                    id="cancelSubmission"
                    stepTitle=""
                    initialStep
                    isLastStep
                    submitText="Submit"
                    showback={true}
                    backurl={organisationId ? `/forms/appsorg?casaorgid=${organisationId}` : "/forms/appsind"}
                    key="cancelSubmission">

                    <AUcallout
                        title=""
                        srOnlyTitle>
                        <h4>Please note</h4>
                        <p>If you submit this cancellation, this cannot be undone.</p>
                        <p>
                            {submissionDetails?.data?.serviceCatalogueName} <br/>
                            Case number: {caseNumber}
                        </p>
                    </AUcallout>

                    <Question
                        id="cancellationReason"
                        key="cancellationReason"
                        type="textarea"
                        label="Please provide a reason for cancellation"
                        minLength={50}
                        maximumlength={4000}
                        stepid="cancelSubmission"/>
                </Step>
            </Application>
        </>
    );
};

SubmissionCancellation.propTypes = {
    dispatch: PropTypes.func,
    B2CSettings: PropTypes.object,
    envdata: PropTypes.object,
    previousPageLink: PropTypes.string,
    formProperties: PropTypes.object,
    verificationViewData: PropTypes.object,
    match: PropTypes.object
};

export default SubmissionCancellation;